// @em/blocks — the block config schema. Every page-widget's type-specific settings
// live in one loose `WidgetConfig` JSON blob (persisted in page_widget.config); a
// small family of string-literal unions names the per-type option values. Pure
// types, no runtime — shared by the authoring app, this package's views, and the
// SSR event website.
//
// ⚠ `import type` — this file stays runtime-free, so the share network union comes
// across as a type only and `shareTargets.ts`'s table is not dragged in behind it.
import type { WidgetLinkAction, WidgetLinkKind } from './linkTargets';
import type { ShareNetworkId } from './shareTargets';

/** The persisted `widgetType` families the CMS UI writes. */
export type WidgetType =
  | 'html'
  | 'markdown'
  | 'image'
  | 'image_gallery'
  | 'image_carousel'
  | 'embed'
  | 'weather'
  | 'map'
  | 'countdown'
  | 'agenda'
  | 'guest_list'
  | 'links';

/** A single link button in a links block. */
export interface WidgetLink {
  label: string;
  /** The address of a `url` link (the only kind before A2e #85); empty for the other kinds. */
  url: string;
  /** A2e #85 — what the button links to (linkTargets.ts). Absent = `url`. */
  kind?: WidgetLinkKind | null;
  /** `page`: the event page's ID (not its path — a renamed page keeps working). */
  pageId?: string | null;
  /** `block`: a block on the same page, by ID. */
  blockId?: string | null;
  /** `action`: register / mine / calendar. */
  action?: WidgetLinkAction | null;
  /** Optional leading icon (a registered AppIcon name). */
  icon?: string | null;
  /** Per-button style + colours. */
  variant?: ButtonVariant;
  bgColor?: string | null;
  textColor?: string | null;
}

/** Link-button appearance. */
export type ButtonVariant = 'filled' | 'light' | 'outline';
export type ButtonLayout = 'row' | 'stack';

/** Weather block display. */
export type WeatherMode = 'current' | 'forecast';
export type WeatherUnits = 'metric' | 'imperial';

/** Countdown target: to the start, elapsed since start, or to the end. */
export type CountdownTarget = 'start' | 'sinceStart' | 'end';

/** Which guests a guest-list block shows. */
/**
 * Where a people block (guests / hosts) draws from. The first four are the guests block's original single choice.
 * `filter` (2026-10-21) = the combined filter: `guestRoles` ∧ (`guestIds` ∪ `guestGroupIds`, when either is set) ∧
 * `guestRsvpStatuses` — read through `peopleFilterOf`, which maps the older sources onto it.
 */
export type GuestSource = 'all' | 'attending' | 'group' | 'selected' | 'filter';

/** How a guest-list block presents its guests. */
export type GuestLayout = 'cards' | 'list';

/** Avatar size for the guests block's card layout. */
export type GuestAvatarSize = 'sm' | 'md' | 'lg' | 'xl';

/** Where a stat's number comes from: a fixed value, or a live event count. */
export type StatSource = 'static' | 'attending' | 'guests';

/** A single stat in a stats block. */
export interface WidgetStat {
  value: string;
  label: string;
  /** 'static' (default) uses `value`; live sources count event guests. */
  source?: StatSource;
}

/** Divider block style: a line, blank space, or a line with a centered icon. */
export type DividerStyle = 'line' | 'space' | 'icon';

/** Highlights block presentation. */
export type HighlightLayout = 'chips' | 'list';

/** Tickets block presentation. */
export type TicketLayout = 'cards' | 'list';

/** Quote block presentation: a bordered card, a big pull-quote, or plain. */
export type QuoteStyle = 'card' | 'large' | 'plain';

/** One content card in a cards block. */
export interface WidgetCard {
  title: string;
  /** Body copy — authored as Markdown. */
  description?: string | null;
  /** Optional link the whole card points to. */
  url?: string | null;
  /** Optional leading icon (a registered AppIcon name). */
  icon?: string | null;
  /** Per-card colours (fall back to the block's, then the theme). */
  bgColor?: string | null;
  textColor?: string | null;
}

/** Which venue a map block shows. */
export type MapSource = 'event' | 'slot' | 'dynamic';

/** Aspect ratio for an embed / media frame ('auto' = fixed height instead). */
export type EmbedAspect = '16:9' | '4:3' | '1:1' | '21:9' | '9:16' | 'auto';

/** How a text block's body is authored (all stored as html/markdown content). */
export type TextBlockKind = 'text' | 'markdown' | 'richtext' | 'html';

/**
 * #62 P6(d) — where a text block's words come from. `eventSummary` = the event's own Overview (event.summary), LIVE: the
 * server fills `content` from it on every public read, so editing the Overview in Details changes the website with no
 * block to edit. Absent / null = the block's own `content`, as always. ⚠ The server's constant is the source of truth
 * (eventsmagic-api `boundary/pageWidgetText.ts`); this mirrors it.
 */
export const TEXT_SOURCE_EVENT_SUMMARY = 'eventSummary' as const;
export type TextBlockSource = typeof TEXT_SOURCE_EVENT_SUMMARY;

/** How an image block presents its images. */
export type ImageLayout = 'single' | 'gallery' | 'carousel';

/** Gallery packing: masonry keeps natural aspect ratios; uniform crops to a grid. */
export type GalleryStyle = 'masonry' | 'uniform';

/** Transition between carousel/gallery images. */
export type ImageEffect = 'slide' | 'fade' | 'zoom' | 'none';

export interface WidgetImage {
  /** Stored ms_storage file id when picked from the user's files. */
  uploadedFileId?: string | null;
  /** Display URL captured at authoring time (stored file or external). */
  url: string;
  caption?: string | null;
}

/** Per-block styling + type-specific options, persisted in page_widget.config. */
export interface WidgetConfig {
  /** Text block: which editor produced `content`. */
  textKind?: TextBlockKind;
  /** Text block: `eventSummary` = show the event's Overview, live (see TEXT_SOURCE_EVENT_SUMMARY). */
  textSource?: TextBlockSource | null;
  /** Image block options. */
  images?: WidgetImage[];
  layout?: ImageLayout;
  /** Gallery-only: masonry (default) keeps natural aspect ratios; uniform crops. */
  galleryStyle?: GalleryStyle;
  effect?: ImageEffect;
  allowFullscreen?: boolean;
  autoplay?: boolean;
  /** Carousel auto-advance interval (seconds) when autoplay is on. */
  autoplaySeconds?: number;
  /** Embed block: a URL to embed (YouTube/Vimeo/maps/weather/…) OR raw HTML. */
  embedUrl?: string | null;
  embedHtml?: string | null;
  /** Frame aspect ratio; 'auto' uses embedHeight instead. */
  aspectRatio?: EmbedAspect;
  /** Fixed height (px) when aspectRatio is 'auto'. */
  embedHeight?: number | null;
  /** Cap the block width (px); empty = full width. */
  maxWidth?: number | null;
  /** Weather block: current-day summary or a multi-day forecast + units. */
  weatherMode?: WeatherMode;
  weatherDays?: number;
  weatherUnits?: WeatherUnits;
  /** Map block: which venue + zoom + which details to show. */
  mapSource?: MapSource;
  /** When mapSource='slot', the time slot whose venue to show. */
  mapSlotId?: string | null;
  mapZoom?: number;
  showAddress?: boolean;
  showContact?: boolean;
  showWebsite?: boolean;
  /** Countdown block: what it counts + the message shown once it's reached. */
  countdownTarget?: CountdownTarget;
  countdownDoneText?: string | null;
  /** Schedule/agenda block: show each slot's venue + description. */
  scheduleShowVenue?: boolean;
  scheduleShowDescription?: boolean;
  /**
   * Schedule block: how much it says about each session's people (owner, 2026-10-01 — "so user could expose different
   * level of information"). Speakers default ON (absent = shown, as before this setting existed); their bio, website and
   * social links default OFF — the host opts in, as on the guests block. The three details only apply while speakers show.
   */
  scheduleShowSpeakers?: boolean;
  scheduleShowSpeakerBio?: boolean;
  scheduleShowSpeakerWebsite?: boolean;
  scheduleShowSpeakerSocial?: boolean;
  /**
   * Schedule block: which tracks / rooms it shows (owner, 2026-10-01). 'all' (or absent) = every track; 'selected' = only
   * `scheduleTracks` — plus the sessions with no track, which belong to everyone (`scheduleTracks.ts`). When the block
   * shows more than one track, guests get a picker.
   */
  scheduleTrackMode?: 'all' | 'selected';
  scheduleTracks?: string[];
  /** Guest-list block: which guests + how they're shown. */
  guestSource?: GuestSource;
  /** When guestSource='group', the guest group to show. */
  guestGroupId?: string | null;
  /** When guestSource='selected' (or 'filter'), the specific people to show — in this order. */
  guestIds?: string[];
  /** guestSource='filter': the roles to include ('guest' and the crew roles). Empty/absent = the block's default. */
  guestRoles?: string[];
  /** guestSource='filter': the guest groups to show (anyone in ANY of them; with `guestIds`, either one). */
  guestGroupIds?: string[];
  /** guestSource='filter': the RSVP statuses to show (empty = any). */
  guestRsvpStatuses?: string[];
  /** Show each person's job title and company ("Head of Design, Acme") under their name. */
  guestShowTitle?: boolean;
  guestLayout?: GuestLayout;
  /** Show each guest's RSVP status badge. */
  guestShowRsvp?: boolean;
  /** Show each guest's bio (great for a speaker line-up). */
  guestShowBio?: boolean;
  /** Show each guest's website link. */
  guestShowWebsite?: boolean;
  /** Show each guest's social links. */
  guestShowSocial?: boolean;
  /** Cards layout: how many avatar cards per row (2–6). */
  guestColumns?: number;
  /** Cards layout: avatar size (bigger for featured people / a couple). */
  guestAvatarSize?: GuestAvatarSize;
  /** Guest-list discriminator: 'hosts' = the hosts/organizers variant. */
  guestKind?: 'guests' | 'hosts';
  /** Hosts block: which participant roles to include. */
  hostRoles?: string[];
  /** Hosts block: show each person's role label under their name. */
  guestShowRole?: boolean;
  /** `custom`-type discriminator so stats/divider/highlights/tickets/quote/cards/pois/accommodations/tasks/travel/budget/eventMap/partners share one widgetType. */
  customKind?: 'stats' | 'divider' | 'highlights' | 'tickets' | 'quote' | 'cards' | 'pois' | 'accommodations' | 'signup' | 'poll' | 'tasks' | 'travel' | 'budget' | 'eventMap' | 'partners' | 'partnerPackages' | 'volunteerShifts' | 'artists' | 'prizeWinners' | 'gameScoreboard' | 'myRole' | 'raceResults' | 'contactUs' | 'lostFound' | 'guestbook' | 'share' | 'audio' | 'organizerEvents' | 'organizerFooter' | 'donate' | 'goal';
  /** Race results block: whole event or one wave (ticket type / guest group). */
  raceResultsSource?: 'all' | 'group';
  /** When raceResultsSource='group', the ticket type / guest group id to show. */
  raceResultsGroupId?: string | null;
  /** Race results block: max rows to show (null = all). */
  raceResultsLimit?: number | null;
  /** Race results block: show the wave column. */
  raceResultsShowWave?: boolean;
  /**
   * Partner-PACKAGES block (#A4.5): which sponsorship offerings to show + how.
   *
   * ⚠ Separate keys from the partners block's, on purpose. One block shows who has already signed,
   * the other what is still for sale; sharing `partnerSource` would make changing one silently
   * change the other wherever a page carries both — which a sponsor page usually does.
   */
  partnerPackageSource?: 'all' | 'kind' | 'selected';
  /** When partnerPackageSource='kind', the partner type to show. */
  partnerPackageKind?: string | null;
  /** When partnerPackageSource='selected', the specific OFFERINGS to show, in the host's order. */
  partnerPackageIds?: string[];
  /** Group the list under a heading per partner type. On by default — it is what the block is for. */
  partnerPackageGroupByKind?: boolean;
  /** Show the price on each package. */
  partnerPackageShowPrice?: boolean;
  /** Show how many places are left. */
  partnerPackageShowSlots?: boolean;
  /** Label on the apply button. */
  partnerPackageButtonLabel?: string;
  partnerPackageColumns?: number;

  /**
   * Volunteer-shifts block (#A4.6): which open shifts to list + how. Only shifts the host OPENED for sign-up
   * ever arrive; this narrows them further.
   */
  volunteerSource?: 'all' | 'roles';
  /** When volunteerSource='roles', the volunteer roles whose shifts to show. */
  volunteerRoleIds?: string[];
  /** Group the shifts under their role, with the role's description. On by default. */
  volunteerGroupByRole?: boolean;
  /** Show what each shift earns (issued at check-in). On by default. */
  volunteerShowRewards?: boolean;
  /** Show how many places are left. On by default. */
  volunteerShowSpots?: boolean;
  /** Label on each shift's button. Default "Sign up". */
  volunteerButtonLabel?: string;

  /** Partners/sponsors block: which partners to show + how. */
  partnerSource?: 'all' | 'kind' | 'package' | 'selected';
  /** When partnerSource='kind', the partner type to show. */
  partnerKind?: string | null;
  /** When partnerSource='package', the catalog package to filter by. */
  partnerPackageId?: string | null;
  /** When partnerSource='selected', the specific partners to show. */
  partnerIds?: string[];
  partnerLayout?: 'grid' | 'list';
  /** Grid layout: how many logos/cards per row (desktop; mobile is always 1–2). */
  partnerColumns?: number;
  /** Show each partner's logo. */
  partnerShowLogo?: boolean;
  /** Show each partner's name. */
  partnerShowName?: boolean;
  /** Show each partner's tier label (e.g. "Gold"). */
  partnerShowTier?: boolean;
  /** Show a website link (logo/name link out when a URL exists). */
  partnerShowWebsite?: boolean;
  /** Show social links (from a linked person contact). */
  partnerShowSocials?: boolean;
  /** Artists / lineup block: which booked acts to show + how. */
  artistSource?: 'all' | 'billing' | 'picked';
  /** When artistSource='billing', the billing tier to show (headliner/support/…). */
  artistBilling?: string | null;
  /** When artistSource='picked', the specific bookings to show (host's chosen order). */
  artistIds?: string[];
  /** Photo grid (festival-poster cards) or a time-sorted list. */
  artistLayout?: 'grid' | 'list';
  /** Grid layout: how many cards per row (desktop; mobile is always 1–2). */
  artistColumns?: number;
  /** Show each act's photo. */
  artistShowPhoto?: boolean;
  /** Show each act's blurb. */
  artistShowBlurb?: boolean;
  /** Show each act's stage / room. */
  artistShowStage?: boolean;
  /** Show each act's set time (rendered in the event timezone). */
  artistShowSetTime?: boolean;
  /** Show links (website / Spotify / YouTube / socials) as small icon links. */
  artistShowLinks?: boolean;
  /** Show each act's billing tier as a StatusPill. */
  artistShowBilling?: boolean;
  /**
   * #165 — on a RECURRING event: 'next' (default) shows the lineup of the next date that has acts (with its date), 'all'
   * every act. Ignored on any other event.
   */
  artistDates?: 'next' | 'all';
  /** Prize-winners block: which published rounds to show. */
  prizeWinnerSource?: 'all' | 'picked';
  /** When prizeWinnerSource='picked', the specific rounds to show (host's chosen order). */
  prizeDrawIds?: string[];
  /** Show each winner's prize label (for tiered prizes). */
  prizeWinnerShowPrize?: boolean;
  /** Show published rounds that haven't been drawn yet, with a "coming soon" message. */
  prizeWinnerShowPending?: boolean;
  /** Block-level default "winners coming soon" message, used when a round has none. */
  prizeWinnerPendingText?: string | null;
  /** ROADMAP B26 organizer-events block: ALL the organizer's upcoming events, or only those of this event's CHANNEL. */
  organizerEventsMode?: 'all' | 'channel';
  /** …how many to show (1–24; default 6). */
  organizerEventsLimit?: number | null;
  /** …as cards in a grid, or a compact list. */
  organizerEventsLayout?: 'cards' | 'list';
  /** #204 — the Fundraising block: ONE of the event's causes (null = all of them). */
  donateCauseId?: string | null;
  /** …a Give button on each cause — on unless switched off. Off: a progress display, which stays after a cause closes. */
  donateAllowGifts?: boolean;
  /** …a progress bar (raised of goal) under each cause — on unless switched off. */
  donateShowGoal?: boolean;
  /**
   * …its recent named supporters — off unless switched on. ⓘ They show only when the CAUSE shows donors too: the server
   * sends none otherwise, so this can hide them but never reveal them.
   */
  donateShowDonors?: boolean;
  /** …what each supporter row shows: the name and the amount (each on unless switched off; an amount only when the cause shows amounts). */
  donateShowDonorNames?: boolean;
  donateShowDonorAmounts?: boolean;
  /** …the donor's note: off unless switched on, and only the notes donors offered to the public. */
  donateShowDonorComments?: boolean;
  /** @deprecated A stored `goal` block, read by `fundraisingOptions` as a Fundraising block with Give off. Not written any more. */
  goalCauseId?: string | null;
  goalShowDonors?: boolean;
  goalShowDonorNames?: boolean;
  goalShowDonorAmounts?: boolean;
  goalShowDonorComments?: boolean;
  /** ROADMAP B25 organizer footer block: which of the profile's public facts to show — each on unless switched off. */
  organizerFooterShowWebsite?: boolean;
  organizerFooterShowSocial?: boolean;
  organizerFooterShowEmail?: boolean;
  organizerFooterShowPhone?: boolean;
  organizerFooterShowAddress?: boolean;
  /** Game-scoreboard block: which game's standings to show (empty = the event's first game). */
  gameScoreboardGameId?: string | null;
  /** Show the team standings (in-play / out per team). */
  gameScoreboardShowTeams?: boolean;
  /** Show the feed of eliminated players with their revealed roles. */
  gameScoreboardShowEliminations?: boolean;
  /** Networking Roulette only: show THIS round's pairings ("who's meeting whom") instead of the connections roster. */
  gameScoreboardShowPairings?: boolean;
  /** "My role" block: which game the guest's own role is shown for (empty = the event's first game). */
  myRoleGameId?: string | null;
  /** Accommodations block: which lodging options to show + how. */
  accommodationSource?: 'all' | 'category';
  /** When accommodationSource='category', the lodging category to show. */
  accommodationCategoryId?: string | null;
  accommodationLayout?: 'cards' | 'list';
  /** Cards layout: how many cards per row (desktop; mobile is always 1). */
  accommodationColumns?: number;
  accommodationShowPrice?: boolean;
  accommodationShowContact?: boolean;
  accommodationShowAddress?: boolean;
  /** Show the straight-line distance from the event venue. */
  accommodationShowDistance?: boolean;
  /** Show the group booking / discount code. */
  accommodationShowBookingCode?: boolean;
  /** Show who is staying at each place (the "see everyone's stays" roster) — OFF by default (guest names are private on a public page). */
  accommodationShowRoster?: boolean;
  /** Let guests sign up to stay at a place (guest self-service; wired on the event website). */
  accommodationAllowSignup?: boolean;
  /** Custom label for the accommodation signup button (e.g. "I'm staying here"). */
  accommodationButtonLabel?: string | null;
  /** Close accommodation signups after this date/time (floating wall-clock in the event tz); empty = no deadline. */
  accommodationUntil?: string | null;
  /** Let guests upvote places during planning. */
  accommodationAllowUpvote?: boolean;
  /** Let guests comment / discuss places during planning. */
  accommodationAllowComments?: boolean;
  /** Offer a printable blank accommodation signup form. */
  accommodationAllowPrintBlank?: boolean;
  /** Offer guests a printout of their own accommodation signups. */
  accommodationAllowPrintMine?: boolean;
  /** Signup-list block: which items to show + how + the guest-interaction rules. */
  signupSource?: 'all' | 'category';
  /** When signupSource='category', the signup category to show. */
  signupCategoryId?: string | null;
  signupLayout?: 'cards' | 'list';
  /** Cards layout: how many cards per row (desktop; mobile is always 1). */
  signupColumns?: number;
  /** Show the price / price unit on each item. */
  signupShowPrice?: boolean;
  /** Show remaining/available quantity on each item. */
  signupShowQuantity?: boolean;
  /** Show each item's description. */
  signupShowDescription?: boolean;
  /** Let guests sign up for items (guest self-service; wired on the event website). */
  signupAllowSignup?: boolean;
  /** Close signups after this date/time (ISO); empty = no deadline. */
  signupUntil?: string | null;
  /** Custom label for the signup button (e.g. "Bring this", "Volunteer"). */
  signupButtonLabel?: string | null;
  /** Let guests upvote items during planning. */
  signupAllowUpvote?: boolean;
  /** Let guests comment / discuss items during planning. */
  signupAllowComments?: boolean;
  /** Let guests see everyone's signups (vs only their own). */
  signupShowOthers?: boolean;
  /** Collect payments online for priced items (guest checkout; wired on the event website). */
  signupCollectPayments?: boolean;
  /** Offer a printable blank order form guests can fill in on paper. */
  signupAllowPrintBlank?: boolean;
  /** Offer guests a printout of their own signups with details. */
  signupAllowPrintMine?: boolean;
  /** #68 Lost & found block: the text above its buttons (plain text); who may use it is the event's lost & found settings. */
  lostFoundIntro?: string | null;
  /** Contact Us block: who may send + what the form collects + notification. */
  contactAudience?: 'anyone' | 'recognized';
  /** Intro text shown above the form (plain text). */
  contactIntro?: string | null;
  /** Also collect a phone number (in addition to email). */
  contactCollectPhone?: boolean;
  /** Custom label for the send button (e.g. "Send message", "Ask a question"). */
  contactButtonLabel?: string | null;
  /** Email opted-in crew when a message arrives (also enforced server-side). */
  contactNotifyCrew?: boolean;
  /** Guest-book / comments block: intro shown above the thread. */
  guestbookIntro?: string | null;

  /** Share block (#55): a short line above the buttons. */
  shareIntro?: string | null;
  /**
   * Which networks to offer, in `SHARE_NETWORKS` order.
   *
   * ⚠ ABSENT MEANS ALL; AN EMPTY ARRAY MEANS NONE — see `selectedShareNetworks`. A block
   * the host has never opened has no key at all and must still show something.
   */
  shareNetworks?: ShareNetworkId[] | null;
  /** Offer a QR code of the event link (default true). */
  shareShowQr?: boolean;
  /**
   * Audio block (#72): the music — a stored ms_storage file (its id kept, its url the one played, as the video block does)
   * or a pasted link to an audio file. Required: a block with no music plays nothing and shows its empty note.
   */
  audioUrl?: string | null;
  audioUploadedFileId?: string | null;
  /** An optional SECOND track mixed under the music — rain, the ocean, a crowd. Always loops. */
  audioBackgroundUrl?: string | null;
  audioBackgroundUploadedFileId?: string | null;
  /** Volumes, 0–100 (absent = 80 for the music, 40 for the background). */
  audioVolume?: number | null;
  audioBackgroundVolume?: number | null;
  /**
   * "Autoplay" — browsers refuse sound until the visitor has interacted with the page, so this means: start on the
   * visitor's FIRST tap or key press anywhere on the page. Off = only the Play button starts it.
   */
  audioAutoplay?: boolean;
  /** Repeat the music when it ends. */
  audioLoop?: boolean;
  /** The name shown on the player ("Our first dance"); blank → none. A library pick fills it with the track's title. */
  audioTitle?: string | null;
  /** The moving picture while it plays — `audioVisualizerOf` reads it (absent → bars). */
  audioVisualizer?: 'bars' | 'wave' | 'none' | null;
  /**
   * The credit shown under the player — ⚠ REQUIRED by the CC BY licences most library tracks carry, so a library pick
   * fills it ('"Rain" by InspectorJ — CC BY 4.0') with a link to the track's page. The host may type their own.
   */
  audioCredit?: string | null;
  audioCreditUrl?: string | null;
  /** The licence's own page — CC BY requires a link to the licence, not just its name (review, 2026-10-01). */
  audioCreditLicenseUrl?: string | null;
  audioBackgroundCredit?: string | null;
  audioBackgroundCreditUrl?: string | null;
  audioBackgroundCreditLicenseUrl?: string | null;
  /** Offer a copy-link button (default true). */
  shareShowCopy?: boolean;
  /** The host's own sharing message; blank → "Check out <event title>". */
  shareMessage?: string | null;

  /** GENERIC per-entity comment settings — the SAME shape on every comment-supporting block (guest book,
   *  POI, accommodation, signup, budget, travel, tasks). Enforced authoritatively server-side by
   *  PublicCommentCore. Supersedes the legacy per-block fields (guestbookCanPost / guestbookAudience /
   *  <kind>AllowComments), which are still read as a fallback for pages saved before this shape existed. */
  comments?: {
    /** Whether guests can post/react at all (off = a read-only thread / no discussion). */
    allow?: boolean;
    /** Who may post: any recognized guest ('all'), those with a given RSVP status, or those in a group. */
    audience?: 'all' | 'rsvp_status' | 'group';
    /** When audience='rsvp_status' — the RSVP statuses allowed to post. */
    rsvpStatuses?: string[];
    /** When audience='group' — the guest-group ids allowed to post. */
    groupIds?: string[];
    /** Guest-book only: the event-wide private direct-message policy between guests. 'off' (default) = DMs
     *  disabled; 'all' = any recognized, reachable, non-opted-out guest may message any other; 'groups' = a guest
     *  may only message others who share at least one guest-group with them. */
    dm?: {
      mode?: 'off' | 'all' | 'groups';
    };
  };

  /** @deprecated legacy guest-book fields — superseded by `comments` (still read as fallback). */
  guestbookCanPost?: boolean;
  guestbookAudience?: 'all' | 'rsvp_status' | 'group';
  guestbookRsvpStatuses?: string[];
  guestbookGroupIds?: string[];
  /** Poll/quiz block: which questions to show + how + the guest-interaction rules. */
  pollSource?: 'all' | 'category';
  /** When pollSource='category', the poll category to show. */
  pollCategoryId?: string | null;
  pollLayout?: 'cards' | 'list';
  /** Cards layout: how many cards per row (desktop; mobile is always 1). */
  pollColumns?: number;
  /** Show each question's description. */
  pollShowDescription?: boolean;
  /** Show each question's answer options (off = question teaser only). */
  pollShowOptions?: boolean;
  /** Reveal correct answers on quiz questions (usually only after answering). */
  pollShowCorrect?: boolean;
  /** Let guests answer in the block (#147 — guests only; the server refuses an answer where no block allows it). */
  pollAllowAnswer?: boolean;
  /** Custom label for the Save button of a written (free-text) answer. */
  pollButtonLabel?: string | null;
  /** Close the poll after this wall-clock date/time in the event's time zone; empty = no deadline (the server keeps it). */
  pollUntil?: string | null;
  /** Show how many chose each answer, in the block (the server sends the counts only where a block shows them). */
  pollShowResults?: boolean;
  /** #147 — results show only once the guest's party has answered (the server holds the counts back until then). */
  pollResultsAfterAnswer?: boolean;
  /** Points-of-interest block: which POIs to show + how. */
  poiSource?: 'all' | 'category' | 'selected';
  /** When poiSource='category', the category to show. */
  poiCategoryId?: string | null;
  /** When poiSource='selected', the specific POI ids to show. */
  poiIds?: string[];
  poiLayout?: 'cards' | 'list';
  /** Cards layout: how many cards per row (desktop; mobile is always 1). */
  poiColumns?: number;
  poiShowPrice?: boolean;
  poiShowContact?: boolean;
  poiShowAddress?: boolean;
  /** Let guests sign up to attend a POI (guest self-service; wired on the event website). */
  poiAllowSignup?: boolean;
  /** Custom label for the POI signup button (e.g. "I'll go", "Count me in"). */
  poiButtonLabel?: string | null;
  /** Close POI signups after this date/time (floating wall-clock in the event tz); empty = no deadline. */
  poiUntil?: string | null;
  /** Let guests see everyone's POI signups (vs only their own). */
  poiShowOthers?: boolean;
  /** Let guests upvote POIs during planning. */
  poiAllowUpvote?: boolean;
  /** Let guests comment / discuss POIs during planning. */
  poiAllowComments?: boolean;
  /** Offer a printable blank POI signup form guests can fill in on paper. */
  poiAllowPrintBlank?: boolean;
  /** Offer guests a printout of their own POI signups. */
  poiAllowPrintMine?: boolean;
  /** Tasks / appointments block: which to show + how. */
  taskSource?: 'all' | 'category';
  /** When taskSource='category', the category to show. */
  taskCategoryId?: string | null;
  /** Limit to a single type; omit for both tasks and appointments. */
  taskType?: 'task' | 'appointment' | null;
  taskLayout?: 'cards' | 'list';
  /** Cards layout: how many cards per row (desktop; mobile is always 1). */
  taskColumns?: number;
  /** Show the scheduled date/time. */
  taskShowSchedule?: boolean;
  /** Show who is responsible. */
  taskShowResponsible?: boolean;
  /** Show a status badge + tracked/expected time (read-only). */
  taskShowProgress?: boolean;
  /** Allow visitors to run tasks (start / pause / complete) from the event website. */
  taskAllowExecute?: boolean;
  /** Travel / itinerary block: which legs to show + how. */
  travelSource?: 'all' | 'selected';
  /** When travelSource='selected', the specific travel-segment ids to show. */
  travelSegmentIds?: string[];
  /** Show who is travelling on each leg — OFF by default (names are private on a public page). */
  travelShowTravellers?: boolean;
  /** Show each leg's status pill (planned / confirmed / delayed…). */
  travelShowStatus?: boolean;
  /** Show the carrier + service number line (e.g. "Flair F8 622"). */
  travelShowCarrier?: boolean;
  /** Let guests share their own arrival/departure travel (guest self-service; wired on the event website). */
  travelAllowShare?: boolean;
  /** Custom label for the travel share button (e.g. "Share your travel"). */
  travelButtonLabel?: string | null;
  /** Close travel sharing after this date/time (floating wall-clock in the event tz); empty = no deadline. */
  travelUntil?: string | null;
  /** Let guests see everyone's shared travel (vs only their own). */
  travelShowOthers?: boolean;
  /** Budget block: which budget lines to show + how. */
  budgetSource?: 'all' | 'categories';
  /** When budgetSource='categories', the category ids to include. */
  budgetCategoryIds?: string[];
  /** Show paid-vs-remaining under each amount — OFF by default (internal figure). */
  budgetShowPaid?: boolean;
  /** Let guests pledge amounts toward budget items (guest self-service; wired on the event website). */
  budgetAllowContribute?: boolean;
  /** Custom label for the budget contribute button (e.g. "Chip in", "Contribute"). */
  budgetButtonLabel?: string | null;
  /** Close budget contributions after this date/time (floating wall-clock in the event tz); empty = no deadline. */
  budgetUntil?: string | null;
  /** Let guests add a new (off-list) item to contribute toward, in an allowed category. */
  budgetAllowAddItem?: boolean;
  /** Event Map block: which on-site elements to show + how. */
  eventMapSource?: 'all' | 'category';
  /** When eventMapSource='category', the map category to show. */
  eventMapCategoryId?: string | null;
  /** Map height in px for the Event Map block. */
  mapWidgetHeight?: number;
  /** Show each element's linked sessions in its marker popup. */
  mapShowSchedule?: boolean;
  /** Cards block: the cards + how many per row (desktop; mobile is always 1). */
  cards?: WidgetCard[];
  cardColumns?: number;
  /** Quote block: the quotation + optional attribution (author / when / where). */
  quoteText?: string | null;
  quoteAuthor?: string | null;
  /** When it was said — free text (a year, era, or full date). */
  quoteDate?: string | null;
  /** Where it was said — an event, publication, or place. */
  quoteSource?: string | null;
  quoteStyle?: QuoteStyle;
  /** Tickets block: layout + which details to show. */
  ticketLayout?: TicketLayout;
  ticketShowAddons?: boolean;
  ticketShowDescription?: boolean;
  ticketShowAvailability?: boolean;
  ticketShowMembership?: boolean;
  /** Stats block: the numbers + labels, and how many per row. */
  stats?: WidgetStat[];
  statsColumns?: number;
  /** Video block: a YouTube/Vimeo link or a direct video file URL. */
  videoUrl?: string | null;
  /** Set when the video is a stored ms_storage file (→ native player). */
  videoUploadedFileId?: string | null;
  videoAutoplay?: boolean;
  videoMuted?: boolean;
  videoLoop?: boolean;
  /** Divider block: style + (space) height + (icon) the centered icon. */
  dividerStyle?: DividerStyle;
  dividerHeight?: number;
  dividerIcon?: string | null;
  /** Highlights block: chips or a list. */
  highlightLayout?: HighlightLayout;
  /** Links block: the buttons + their appearance. */
  links?: WidgetLink[];
  buttonVariant?: ButtonVariant;
  buttonLayout?: ButtonLayout;
  // Shared styling
  /** Optional uppercase accent label shown above the block title (a section eyebrow). */
  eyebrow?: string | null;
  subtitle?: string | null;
  backgroundColor?: string | null;
  textColor?: string | null;
  /**
   * A2e #88 — the block's own background image ({ url, uploadedFileId, opacity 0–100, position }; backgroundImage.ts),
   * over `backgroundColor`. Premium: the server refuses adding one and leaves it out of the public site on a plan
   * without `site_styling` (while paywalls are on).
   */
  backgroundImage?: { url?: string | null; uploadedFileId?: string | null; opacity?: number | null; position?: string | null } | null;
  /** Simple grid: how many columns the block content flows into (1–4). */
  columns?: number;
  align?: 'left' | 'center' | 'right';
  /** A2e #84 — the block's own font: a KEY from `BLOCK_FONTS` (blockFonts.ts), never CSS. Absent = the theme's. */
  fontKey?: string | null;
  /** A2e #84 — the block's text size: a KEY from `BLOCK_TEXT_SIZES` (`sm` … `xxl`). Absent = the theme's. */
  textSize?: string | null;
  [key: string]: unknown;
}
